import { memo } from 'react'
import { Box, Card, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'

/** Maps legacy variant names to theme palette (design system) */
const VARIANT_MAP = {
  coral: 'primary',
  yellow: 'warning',
  green: 'success',
  blue: 'info',
  purple: 'secondary',
}

/**
 * StatCard component - Clean stat card with theme palette colors (design system)
 * @param {string} title - Card title (e.g., "Active Campaigns")
 * @param {string|number} value - Main value to display (e.g., 3)
 * @param {React.ReactNode} icon - Icon component
 * @param {string} variant - Theme palette: 'primary', 'success', 'warning', 'info', 'error', 'secondary' (legacy: 'coral', 'yellow', 'green', 'blue', 'purple')
 * @param {number} index - For staggered animation
 * @param {string} subtitle - Optional subtitle text (e.g., "+2 this week")
 * @param {string} badge - Optional badge text
 */
export const StatCard = memo(({
  title,
  value,
  icon,
  variant = 'primary',
  index = 0,
  subtitle,
  badge,
}) => {
  const theme = useTheme()
  const paletteKey = VARIANT_MAP[variant] || variant
  const palette = theme.palette[paletteKey] || theme.palette.primary
  // Light mode: use .dark on tinted card backgrounds for WCAG AA (Wisteria .main fails on pale fills).
  const mainColor = theme.palette.mode === 'light' ? palette.dark : palette.main
  const bgColor = alpha(mainColor, 0.1)
  const iconBgColor = alpha(mainColor, 0.15)
  const textColor = theme.palette.mode === 'light' ? '#2D3748' : theme.palette.text.primary

  return (
    <Card
      sx={{
        bgcolor: bgColor,
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: 'none',
        boxShadow: 'none',
        animation: `fadeInUpSm 0.4s ease-out ${index * 0.1}s both`,
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${alpha(mainColor, 0.25)}`,
        },
      }}
    >
      {icon ? (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: iconBgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            color: mainColor,
            '& > svg': {
              fontSize: '1.75rem',
            },
          }}
        >
          {icon}
        </Box>
      ) : null}

      {/* Value */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          mb: 0.5,
          color: textColor,
          fontSize: { xs: '2rem', sm: '2.5rem' },
        }}
      >
        {value}
      </Typography>

      {/* Title */}
      <Typography
        variant="body2"
        sx={{
          color: textColor,
          fontWeight: 500,
          mb: subtitle || badge ? 1 : 0,
        }}
      >
        {title}
      </Typography>

      {/* Optional subtitle or badge */}
      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            color: mainColor,
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        >
          {subtitle}
        </Typography>
      )}

      {badge && (
        <Box
          sx={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            bgcolor: iconBgColor,
            mt: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: mainColor,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          >
            {badge}
          </Typography>
        </Box>
      )}
    </Card>
  )
})
